import { usePublishActionOutcome } from "../ui/ActionConfirmation.js";
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { updateStudent, type StudentEditable } from "../api.js";
import { canManageSchoolDomain, useAuth } from "../auth.js";
import { MessageFamilyButton } from "../inbox/Inbox.js";
import { PaymentTrackingAuthority } from "./PaymentTrackingAuthority.js";
import { StudentTeachers } from "../booking/StudentTeachers.js";
import { TeachingLinkHelp } from "../booking/TeachingLinkHelp.js";

/**
 * The contact details on a student's record, read or corrected.
 *
 * Correcting one is not cosmetic. `student_email`, `parent_email` and the
 * second address decide who receives this child's lesson notes, which is why
 * the form is the owner's and why the two known problems in the imported data
 * are worth fixing by hand rather than by rule: every student created from a
 * booking has the booker's address under `parent_email`, right for a child and
 * wrong for an adult booking for themselves; and the names came across in
 * whatever case they were typed.
 */

interface Props {
  studentId: string;
  student: StudentEditable;
  canEdit: boolean;
  /** Re-reads the page, so the heading and lists follow the record. */
  onSaved: () => void;
}

const FIELDS: Array<{ key: keyof StudentEditable; label: string; type?: string }> = [
  { key: "fullName", label: "Name" },
  { key: "instrument", label: "Instrument" },
  { key: "studentEmail", label: "Student email", type: "email" },
  { key: "parentName", label: "Parent name" },
  { key: "parentEmail", label: "Parent email", type: "email" },
  { key: "secondaryEmail", label: "Second address", type: "email" },
  { key: "parentPhone", label: "Phone", type: "tel" },
  { key: "school", label: "School" },
  { key: "formClass", label: "Form" },
];

export function StudentDetails({ studentId, student, canEdit, onSaved }: Props) {
  const publish = usePublishActionOutcome();
  const { me } = useAuth();
  const canManageSchoolPayers = canManageSchoolDomain(me);
  const owner = me?.roles.includes("owner") ?? false;
  const [form, setForm] = useState<StudentEditable | null>(null);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [paymentTrackerRefreshKey, setPaymentTrackerRefreshKey] = useState(0);


  async function save(event: FormEvent) {
    event.preventDefault();
    if (!form) return;
    setSaving(true);
    setProblem(null);
    try {
      // Every field is sent because every field is shown; a blank one is a
      // deliberate clearing rather than an omission.
      await updateStudent(studentId, form);
      publish({ variant: "success", title: "Student details saved", description: form.fullName });
      setForm(null);
      onSaved();
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "Could not save.");
    } finally {
      setSaving(false);
    }
  }

  if (form) {
    return (
      <form className="panel" onSubmit={save}>
        {problem && <div className="error small">{problem}</div>}

        <div className="fields">
          {FIELDS.map((field) => (
            <label key={field.key}>
              <span className="small muted">{field.label}</span>
              <input
                type={field.type ?? "text"}
                value={(form[field.key] as string | null) ?? ""}
                onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
                autoCapitalize={field.type === "email" ? "none" : undefined}
                spellCheck={field.type === "email" ? false : undefined}
                required={field.key === "fullName"}
              />
            </label>
          ))}

          <label>
            <span className="small muted">Billing payer</span>
            <select
              value={form.billingPayer ?? ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  billingPayer: (e.target.value || null) as typeof form.billingPayer,
                })
              }
            >
              <option value="">Not stated — work it out from the addresses above</option>
              <option value="family">Family — the parent address above</option>
              <option value="self">Self — the student&rsquo;s own address above</option>
              <option value="school">
                {canManageSchoolPayers
                  ? "School — configure under School-funded payer settings"
                  : "School"}
              </option>
            </select>
          </label>
        </div>

        {canManageSchoolPayers && form.billingPayer === "school" && (
          <p className="small muted" style={{ marginTop: 4 }}>
            Attending a school does not by itself mean the school pays — check it has been set
            up under <Link to="/school-payers#school-funded-settings">School-funded payer settings</Link>, or the
            booking will be refused rather than billed to a school that has not agreed to it.
          </p>
        )}

        <label className="row" style={{ marginTop: 12 }}>
          <input
            type="checkbox"
            checked={form.isUnder18}
            onChange={(e) => setForm({ ...form, isUnder18: e.target.checked })}
          />
          <span className="small">Under 18</span>
        </label>

        <div className="row" style={{ marginTop: 14 }}>
          <button type="submit" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" className="quiet" disabled={saving} onClick={() => setForm(null)}>
            Cancel
          </button>
          <span className="small muted">
            Addresses here decide who receives this student&rsquo;s lesson notes.
          </span>
        </div>
      </form>
    );
  }

  return (
    <>
      {owner && <MessageFamilyButton studentId={studentId} studentName={student.fullName} />}
      <div className="panel">
        <table>
          <tbody>
            <tr>
              <td className="muted small">Instrument</td>
              <td>{student.instrument ?? "—"}</td>
            </tr>
            <tr>
              <td className="muted small">Parent</td>
              <td>{student.parentName ?? "—"}</td>
            </tr>
            <tr>
              <td className="muted small">Student email</td>
              <td>{student.studentEmail ?? "—"}</td>
            </tr>
            <tr>
              <td className="muted small">Parent email</td>
              <td>{student.parentEmail ?? "—"}</td>
            </tr>
            {student.secondaryEmail && (
              <tr>
                <td className="muted small">Second address</td>
                <td>{student.secondaryEmail}</td>
              </tr>
            )}
            <tr>
              <td className="muted small">Phone</td>
              <td>{student.parentPhone ?? "—"}</td>
            </tr>
            <tr>
              <td className="muted small">School</td>
              <td>
                {student.school ?? "—"}
                {student.formClass && ` · ${student.formClass}`}
              </td>
            </tr>
            <tr>
              <td className="muted small">Under 18</td>
              <td>{student.isUnder18 ? "Yes" : "No"}</td>
            </tr>
            <tr>
              <td className="muted small">Billing payer</td>
              <td>
                {student.billingPayer === "family" && "Family"}
                {student.billingPayer === "self" && "Self"}
                {student.billingPayer === "school" && "School"}
                {!student.billingPayer && (
                  <span className="muted">Not stated — worked out from the addresses above</span>
                )}
              </td>
            </tr>
          </tbody>
        </table>

        {canEdit && (
          <button
            className="quiet small"
            style={{ marginTop: 12 }}
            onClick={() => setForm({ ...student })}
          >
            Edit details
          </button>
        )}
      </div>
      {owner && <StudentTeachers studentId={studentId} onChanged={() => {
        onSaved();
        setPaymentTrackerRefreshKey(key => key + 1);
      }} />}
      {!owner && me?.roles.includes("tutor") && (
        <TeachingLinkHelp studentId={studentId} studentName={student.fullName} onLinked={onSaved} />
      )}
      <PaymentTrackingAuthority studentId={studentId} refreshKey={paymentTrackerRefreshKey} />
    </>
  );
}
